/*
 * File: StudentSection.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { SerializedEditorState } from 'lexical';

import type { CoursePeriod } from 'api/hooks/private/periods/GetCoursePeriod';

import { ContactList } from '../ContactInfo';
import { DriveHistory, DriveHistoryItem, DriveHistorySummaryStats, transformDriveHistoryItems } from '../DriveHistory';
import InstructorNotes from '../Instructors/InstructorNotes';
import AttendanceButtons from './AttendanceButtons';
import StudentHeader from './StudentHeader';
import StudentInfo from './StudentInfo';
import StudentSectionSkeleton from './StudentSectionSkeleton';
import { useStudentSectionData } from './useStudentSectionData';

interface StudentSectionProps {
  enrollment: CoursePeriod['period_enrollments'][0] | undefined;
}

export default function StudentSection({ enrollment }: StudentSectionProps) {
  // Use custom hook to fetch and transform all data
  const { studentUser, parents, driveLabel, allPeriodEnrollments, driveHistoryStats, balanceStatus, isLoading } =
    useStudentSectionData({
      enrollment,
    });

  // Show skeleton while loading
  if (isLoading) {
    return <StudentSectionSkeleton />;
  }

  // Return null if no data after loading completes
  if (!enrollment || !studentUser) {
    return null;
  }

  // Transform drive history data
  const driveHistoryItems = transformDriveHistoryItems(allPeriodEnrollments);

  return (
    <>
      {/* Header - Name and Enrollment Type */}
      <StudentHeader
        student={studentUser}
        driveLabel={driveLabel}
        enrollmentType={enrollment.enrollment_type}
        coursePeriodId={enrollment.course_period_id}
      />

      <Stack gap={2}>
        {/* Student Details - Permit and Balance */}
        <StudentInfo student={studentUser} balanceStatus={balanceStatus} />

        <Divider />

        {/* Attendance Buttons */}
        <AttendanceButtons enrollment={enrollment} />

        <Divider />

        {/* Contact Info Section */}
        <ContactList student={studentUser} parents={parents} />

        <Divider />

        {/* Drive History Section */}
        <DriveHistory
          summaryStats={
            <DriveHistorySummaryStats
              completedCount={driveHistoryStats.completedCount}
              noShowCount={driveHistoryStats.noShowCount}
              noPermitCount={driveHistoryStats.noPermitCount}
              upcomingCount={driveHistoryStats.upcomingCount}
              canceledCount={driveHistoryStats.canceledCount}
            />
          }
          items={driveHistoryItems}
          renderItem={(item) => (
            <DriveHistoryItem
              key={item.id}
              driveLabel={item.driveLabel}
              date={item.date}
              startTime={item.startTime}
              instructors={item.instructors}
              attendance={item.attendance}
              isCanceled={item.isCanceled}
              notes={item.notes}
            />
          )}
        />

        <Divider />

        {/* Instructor Notes */}
        <InstructorNotes
          studentId={studentUser.id}
          periodId={enrollment.course_period_id}
          notes={enrollment.notes as SerializedEditorState | null}
          notesUpdatedAt={enrollment.notes_updated_at}
          notesUpdatedBy={enrollment.notes_updated_by_user}
          privateNotes={enrollment.private_notes as SerializedEditorState | null}
          privateNotesUpdatedAt={enrollment.private_notes_updated_at}
          privateNotesUpdatedBy={enrollment.private_notes_updated_by_user}
        />
      </Stack>
    </>
  );
}
